/* eslint-disable @typescript-eslint/no-var-requires */
import { nanoid } from 'nanoid';
import { finalsList, finalsMapping } from '../kinds/webview/finals';
import { questionsList, questionsMapping } from '../kinds/webview/questions';
import { welcomesList, welcomesMapping } from '../kinds/webview/welcomes';
import { Final as FinalType, Poll } from '../types/webview.generated';
import AdminEngine from './adminEngine';
import { GROUPS_TRAIT, SchemaEngine, WELCOME_SCREEN_TRAIT } from './index';
import {
  createPollSchema,
  createQuestionDeckSchema,
  createQuestionGroupSchema,
  createQuestionSetSchema,
  listToDescription,
} from './schema';

const screensMapping = {} as const;

const flavours = ['default', 'mobile_welcome', 'mobile_bank'] as const;

export class WebviewEngine
  extends AdminEngine<
    typeof questionsMapping,
    typeof finalsMapping,
    typeof welcomesMapping,
    typeof screensMapping,
    typeof flavours
  >
  implements SchemaEngine
{
  protected schemaTraits = [WELCOME_SCREEN_TRAIT, GROUPS_TRAIT];
  protected questionsMapping = questionsMapping;
  protected finalsMapping = finalsMapping;
  protected screensMapping = screensMapping;
  protected welcomesMapping = welcomesMapping;
  protected groupSchema = createQuestionGroupSchema();
  protected deckSchema = createQuestionDeckSchema();
  protected setSchema = createQuestionSetSchema();
  protected flavours = flavours;

  getTemplate() {
    return 'webview' as const;
  }

  getSchema() {
    return createPollSchema({
      welcomesSchema: listToDescription(welcomesList),
      questionsSchema: listToDescription(questionsList),
      finalsSchema: listToDescription(finalsList),
      rootSchemaProps: {
        progressBar: { enum: ['line', 'invisible'] },
        closeable: { type: 'boolean' },
        canGoBack: { type: 'boolean' },
        flavour: { enum: flavours },
      },
    });
  }

  generatePoll({
    questions = this.generateDefaultQuestions(),
    finals = this.generateDefaultFinals(),
    groups = [],
    questionDecks = [],
    answerDecks = [],
    pixels = [],
    sets = [],
    welcome = [],
    ...override
  }: Partial<Poll> = {}): Poll {
    return this.normalizePoll({
      closeable: true,
      id: nanoid(10),
      awardType: 'AWARD_SCORES',
      template: this.getTemplate(),
      pixels,
      questions,
      finals,
      welcome,
      groups,
      questionDecks,
      answerDecks,
      progressBar: 'line',
      ...override,
    });
  }

  generateDefaultQuestions() {
    const { generateQuestion, generateAnswer } = this.questionsMapping['POLL_SINGLE'].generator;
    return [generateQuestion([generateAnswer({ text: 'Ответ 1' }), generateAnswer({ text: 'Ответ 2' })])];
  }

  generateDefaultFinals(): FinalType[] {
    return [
      {
        id: nanoid(10),
        data: {
          type: 'FINAL_CLICK_PAGE' as const,
          footer: '',
          text: '',
          alignContent: 'center',
          offer: 'Спасибо!',
          cta: '',
          ctaUrl: '',
          isDefault: false,
          hasAutoClose: false,
          logicOperationType: 'OR',
          logicRules: [],
        },
      },
    ];
  }

  generateFinal(overrides: Partial<FinalType['data']> = {}): FinalType {
    return {
      id: nanoid(10),
      data: {
        type: 'FINAL_CLICK_PAGE' as const,
        alignContent: 'center',
        offer: 'Спасибо!',
        isDefault: false,
        hasAutoClose: false,
        logicOperationType: 'OR',
        logicRules: [],
        ...overrides,
      },
    };
  }
}

export const engine = new WebviewEngine();
